import { useState, useEffect, useCallback } from 'react';
import { getApiBaseUrl, createApiHeaders } from '../../../../config/api';
import { debugWarn } from '../../../../utils/debugLog';

/**
 * Hook for managing template statistics.
 *
 * @param {Array} executions - Array of execution objects
 * @param {string} templateId - Template ID
 * @returns {Object} Template statistics, loading state, and refetch function
 */
export const useTemplateStats = (executions, templateId) => {
  const [templateStats, setTemplateStats] = useState({
    totalExecutions: 0,
    successRate: 0,
    averageRating: 0,
    totalRatings: 0,
    lastExecuted: null,
  });
  const [loading, setLoading] = useState(false);

  const calculateFromExecutions = useCallback(() => {
    if (!executions || executions.length === 0) {
      setTemplateStats({
        totalExecutions: 0,
        successRate: 0,
        averageRating: 0,
        totalRatings: 0,
        lastExecuted: null,
      });
      return;
    }

    const totalExecutions = executions.length;
    const successfulExecutions = executions.filter(e => e.success).length;
    const successRate =
      totalExecutions > 0 ? (successfulExecutions / totalExecutions) * 100 : 0;

    // Extract ratings from executions if available
    const executionsWithRatings = executions.filter(e => e.rating != null);
    const totalRatings = executionsWithRatings.length;
    const averageRating =
      totalRatings > 0
        ? executionsWithRatings.reduce((sum, e) => sum + (e.rating || 0), 0) /
          totalRatings
        : 0;

    const lastExecuted =
      executions.length > 0 ? executions[0].created_at : null;

    setTemplateStats({
      totalExecutions,
      successRate,
      averageRating,
      totalRatings,
      lastExecuted,
    });
  }, [executions]);

  const fetchAnalytics = useCallback(async () => {
    if (!templateId) {
      // Fallback to execution-based stats if no templateId
      calculateFromExecutions();
      return;
    }

    try {
      setLoading(true);
      const headers = createApiHeaders();
      const response = await fetch(
        `${getApiBaseUrl()}/api/v1/templates/analytics/templates/${templateId}`,
        { headers }
      );

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.template) {
          const analytics = data.template;
          // Extract ratings from analytics
          const avgRating =
            analytics.average_rating || analytics.avg_rating || 0;
          const totalRatings =
            analytics.total_ratings || analytics.rating_count || 0;
          const totalExecutions =
            analytics.total_executions || executions?.length || 0;
          const successRate =
            analytics.success_rate ||
            (executions && executions.length > 0
              ? (executions.filter(e => e.success).length / executions.length) *
                100
              : 0);

          setTemplateStats({
            totalExecutions: totalExecutions,
            successRate: successRate,
            averageRating: avgRating,
            totalRatings: totalRatings,
            lastExecuted:
              analytics.last_executed ||
              (executions && executions.length > 0
                ? executions[0].created_at
                : null),
          });
          return;
        }
      }
      // Fallback to execution-based stats if analytics endpoint fails
      calculateFromExecutions();
    } catch (error) {
      debugWarn(
        'Failed to fetch analytics, using execution-based stats:',
        error
      );
      calculateFromExecutions();
    } finally {
      setLoading(false);
    }
  }, [templateId, executions, calculateFromExecutions]);

  useEffect(() => {
    if (templateId) {
      fetchAnalytics();
    } else {
      calculateFromExecutions();
    }
  }, [templateId, fetchAnalytics, calculateFromExecutions]);

  return {
    templateStats,
    loading,
    refetch: fetchAnalytics,
  };
};
